import React, { useRef, useState, useEffect, forwardRef } from "react";
import Slider from "react-slick";
import { useIsMobile, useLoadOnVisible, useIsVisible } from "../../hooks";
import { motion } from "framer-motion";
import ImageLoader from "../common/ImageLoader";
import { applyVideoPlayback } from "../common/videoPlayback.helpers";

/**
 * @returns {JSX.Element}
 * @constructor
 */

const AirInsights = ({ className = "", title, data }) => {
  const isMobile = useIsMobile(769);
  const sliderRef = useRef(null);
  const sliderWrapRef = useRef(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(0);
  const [slideDuration, setSlideDuration] = useState(
    !data[0]?.videoUrl ? 4000 : data[0]?.duration ?? 4000
  );
  const [progress, setProgress] = useState(-100);
  const [slideInView, setSlideInView] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const isSliderInView = useLoadOnVisible(sliderWrapRef, { threshold: 0.2 });

  useEffect(() => {
    if (slideInView) return;
    setSlideInView(isSliderInView);
  }, [isSliderInView]);

  useEffect(() => {
    if (!isMobile) {
      setSlideDuration(0);
      return;
    }
    const currentItem = data[nextIndex];
    const duration = !currentItem?.videoUrl ? 4000 : currentItem?.duration ?? 4000;
    setSlideDuration(duration);
  }, [isMobile, nextIndex, data]);

  useEffect(() => {
    if (!slideInView) return;
    const currentVideo = document.querySelector(
      ".air-insights__slider .slick-current .air-insights__video",
    );

    if (!isMobile) return;

    const allVideos = document.querySelectorAll(
      ".air-insights__slider .air-insights__video",
    );
    allVideos.forEach((video) => {
      video.pause();
      video.currentTime = 0;
    });

    const currentItem = data[currentVideoIndex];
    const hasVideo = currentItem?.videoUrl;
    const hasImage = currentItem?.image && typeof currentItem.image === "object" && currentItem.image.lg;

    // Handle image-only slides (no video)
    if (!hasVideo && hasImage) {
      setIsVideoPlaying(false);
      setProgress(-100);

      // Animate progress bar
      const duration = slideDuration;
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progressPercent = (elapsed / duration) * 100;
        setProgress(Math.min(progressPercent - 100, 0));

        if (progressPercent >= 100) {
          clearInterval(interval);
          setProgress(0);
          // Move to next slide
          setTimeout(() => {
            if (sliderRef.current && sliderRef.current.slickNext) {
              sliderRef.current.slickNext();
            }
          }, 200);
        }
      }, 50);

      return () => clearInterval(interval);
    }

    // Add event listeners to current video
    if (currentVideo) {
      const handleVideoEnd = () => {
        console.log("Video ended, moving to next slide");
        setIsVideoPlaying(false);
        setProgress(0);
        // Move to next slide
        setTimeout(() => {
          if (sliderRef.current && sliderRef.current.slickNext) {
            console.log("Calling slickNext()");
            sliderRef.current.slickNext();
          } else {
            console.log("sliderRef.current is null or slickNext not available");
          }
        }, 200);
      };

      const handleVideoPlay = () => {
        setIsVideoPlaying(true);
        setProgress(-100); // Start progress from -100% to prevent flicker
      };

      const handleVideoPause = () => {
        setIsVideoPlaying(false);
      };

      const handleTimeUpdate = () => {
        if (currentVideo.duration) {
          const progressPercent =
            (currentVideo.currentTime / currentVideo.duration) * 100;
          setProgress(Math.min(progressPercent - 100, 0));
        }
      };

      // Remove any existing listeners first
      currentVideo.removeEventListener("ended", handleVideoEnd);
      currentVideo.removeEventListener("play", handleVideoPlay);
      currentVideo.removeEventListener("pause", handleVideoPause);
      currentVideo.removeEventListener("timeupdate", handleTimeUpdate);

      // Add new listeners
      currentVideo.addEventListener("ended", handleVideoEnd);
      currentVideo.addEventListener("play", handleVideoPlay);
      currentVideo.addEventListener("pause", handleVideoPause);
      currentVideo.addEventListener("timeupdate", handleTimeUpdate);

      setTimeout(() => {
        currentVideo.play();
      }, 200);

      // Cleanup function
      return () => {
        currentVideo.removeEventListener("ended", handleVideoEnd);
        currentVideo.removeEventListener("play", handleVideoPlay);
        currentVideo.removeEventListener("pause", handleVideoPause);
        currentVideo.removeEventListener("timeupdate", handleTimeUpdate);
      };
    }
  }, [currentVideoIndex, isMobile, slideInView, nextIndex, slideDuration, data]);

  useEffect(() => {
    if (!isVideoPlaying) return;

    const currentVideo = document.querySelector(
      ".air-insights__slider .slick-current .air-insights__video",
    );

    if (!currentVideo) return;

    const updateProgress = () => {
      if (currentVideo.duration) {
        const progressPercent =
          (currentVideo.currentTime / currentVideo.duration) * 100;
        setProgress(Math.min(progressPercent - 100, 0));
      }
    };

    // Update progress immediately
    updateProgress();

    const interval = setInterval(updateProgress, 100); // Update every 100ms
    return () => clearInterval(interval);
  }, [isVideoPlaying, currentVideoIndex]);

  const settings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 500,
    autoplay: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    centerMode: true,
    afterChange: (idx) => {
      setCurrentVideoIndex(idx);
      setProgress(-100);
      const currentItem = data[idx];
      const duration = !currentItem?.videoUrl ? 4000 : currentItem?.duration ?? 4000;
      setSlideDuration(duration);
    },
    beforeChange: (idx, nextIdx) => {
      setNextIndex(nextIdx);
      const nextItem = data[nextIdx];
      const duration = !nextItem?.videoUrl ? 4000 : nextItem?.duration ?? 4000;
      setSlideDuration(duration);
    },
  };
  return (
    <section className={`air-insights ${className}`}>
      <div className="air-insights__card">
        <div className="container">
          <div className="air-insights__header">
            <motion.h2
              className="air-insights__heading"
              initial={{ opacity: 0, y: 20 }}
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.02 }}
              variants={{
                visible: { opacity: 1, y: 0 },
                hidden: { opacity: 0, y: 20 },
              }}
            >
              {title}
            </motion.h2>
            <motion.p
              className="air-insights__short-intro"
              initial={{ opacity: 0, y: 20 }}
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.03 }}
              variants={{
                visible: { opacity: 1, y: 0 },
                hidden: { opacity: 0, y: 20 },
              }}
            >
              It’s impossible to know WTF is in your air—until now. Mila’s got 8
              smart sensors on the scene, <br /> and they don’t miss a thing.
            </motion.p>
          </div>
          {isMobile ? (
            <div className="air-insights__slider-wrapper" ref={sliderWrapRef}>
              {isSliderInView && (
                <Slider
                  className="air-insights__slider"
                  {...settings}
                  ref={sliderRef}
                  style={{ "--progress": `${progress}%` }}
                >
                  {data.map((item, index) => (
                    <Item {...item} key={index} isMobile={isMobile} />
                  ))}
                </Slider>
              )}
            </div>
          ) : (
            <div className="air-insights__grid">
              {data.map((item, index) => (
                <Item {...item} key={index} index={index} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Item = forwardRef(
  ({ videoUrl, title, content, index, isMobile, poster, image }, ref) => {
    const itemRef = useRef(null);
    const videoRef = useRef(null);
    const isInView = useLoadOnVisible(itemRef, { rootMargin: "200px" });
    // Desktop renders a grid of many looping videos; pause each when it leaves
    // the viewport (WEB-2593). Mobile is a single-video slider driven
    // imperatively by the parent, so leave its playback alone.
    const isVisible = useIsVisible(itemRef, { rootMargin: "50px" });

    useEffect(() => {
      if (isMobile) return;
      applyVideoPlayback(videoRef.current, { isLoaded: isInView, isVisible });
    }, [isMobile, isInView, isVisible]);

    // Keep the internal ref while still honoring a ref forwarded by a parent.
    const setVideoRef = (node) => {
      videoRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    return (
      <motion.div
        className="air-insights__item"
        key={title}
        initial={{ opacity: 0 }}
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.01 * index }}
        variants={{
          visible: { opacity: 1 },
          hidden: { opacity: 0 },
        }}
        ref={itemRef}
      >
        <div className="air-insights__content">
          <h3 className="air-insights__title">{title}</h3>

          <div className="air-insights__text">
            <p>{content}</p>
          </div>
        </div>
        <div className="air-insights__media">
          {image && typeof image === "object" && image.lg && image.sm && (
            <ImageLoader
              className="air-insights__image"
              image={image.lg.src}
              smallImage={image.sm.src}
              alt={image.lg.alt}
              placeholderAspectRatio={
                isMobile && image.sm.width && image.sm.height
                  ? `${image.sm.width}/${image.sm.height}`
                  : image.lg.width && image.lg.height
                  ? `${image.lg.width}/${image.lg.height}`
                  : undefined
              }
            />
          )}
          {videoUrl && (
            <video
              src={isInView ? videoUrl : undefined}
              preload="none"
              className="air-insights__video"
              playsInline
              poster={poster}
              loop={!isMobile}
              muted
              ref={setVideoRef}
            ></video>
          )}
        </div>
      </motion.div>
    );
  },
);

export default AirInsights;
